import { cn } from "@breakout/ui/utils"
import { Link } from "@tanstack/react-router"
import type { LucideIcon } from "lucide-react"
import { motion } from "motion/react"

const ICON_BG_STYLES = {
	gray: "bg-bg-tertiary dark:bg-white/5",
	slate: "bg-bg-tertiary dark:bg-white/5",
	amber: "bg-amber-100 dark:bg-amber-900/30",
	red: "bg-red-100 dark:bg-red-900/30",
	none: "",
} as const

const ICON_COLOR_STYLES = {
	gray: "text-text-muted dark:text-white/70",
	slate: "text-text-muted dark:text-white/70",
	amber: "text-amber-500 dark:text-amber-400",
	red: "text-red-500 dark:text-red-400",
	none: "text-text-muted/60 dark:text-white/50",
} as const

interface EmptyStateProps {
	/** Icon to display */
	icon: LucideIcon
	/** Main heading text */
	title: string
	/** Description text */
	description: string
	/** Optional primary action button/link */
	action?: {
		label: string
		href?: string
		onClick?: () => void
	}
	/** Optional secondary action (ghost/text style) */
	secondaryAction?: {
		label: string
		href?: string
		onClick?: () => void
	}
	/** Additional className for the container */
	className?: string
	/** Icon wrapper background color */
	iconBg?: "gray" | "slate" | "amber" | "red" | "none"
	/** Compact variant with less padding */
	compact?: boolean
}

/**
 * Standardized empty state component.
 *
 * Displays a centered card with icon, title, description, and optional action.
 * Used when there's no data to display (no positions, no activity, etc.).
 *
 * @example
 * <EmptyState
 *   icon={Target}
 *   title="No Positions Yet"
 *   description="Start trading to see your positions here"
 *   action={{ label: "Browse Markets", href: "/" }}
 * />
 */
export function EmptyState({
	icon: Icon,
	title,
	description,
	action,
	secondaryAction,
	className,
	iconBg = "gray",
	compact = false,
}: EmptyStateProps) {
	return (
		<motion.div
			animate={{ opacity: 1, scale: 1 }}
			className={cn(
				"rounded-2xl border border-border-subtle bg-bg-card text-center shadow-sm dark:border-border-subtle",
				compact ? "p-6" : "p-10",
				className,
			)}
			initial={{ opacity: 0, scale: 0.95 }}
			transition={{ duration: 0.15, ease: "easeOut" }}
		>
			{iconBg === "none" ? (
				<Icon
					aria-hidden="true"
					className={cn("mx-auto mb-3 size-10", ICON_COLOR_STYLES[iconBg])}
				/>
			) : (
				<div
					className={cn(
						"mx-auto mb-4 flex size-16 items-center justify-center rounded-full",
						ICON_BG_STYLES[iconBg],
					)}
				>
					<Icon aria-hidden="true" className={cn("size-8", ICON_COLOR_STYLES[iconBg])} />
				</div>
			)}
			<h3 className="mb-1 text-balance font-display font-semibold text-lg text-text-primary">
				{title}
			</h3>
			<p className="text-pretty text-sm text-text-muted dark:text-white/60">{description}</p>
			{(action || secondaryAction) && (
				<div className={cn("flex flex-col items-center gap-2", compact ? "mt-4" : "mt-5")}>
					{action &&
						(action.href ? (
							<Link
								className="inline-flex cursor-pointer items-center rounded-xl bg-text-primary px-5 py-2.5 font-semibold text-bg-primary text-sm transition-all hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-text-primary focus-visible:ring-offset-2 active:scale-[0.98]"
								to={action.href}
							>
								{action.label}
							</Link>
						) : (
							<button
								className="inline-flex cursor-pointer items-center rounded-xl bg-text-primary px-5 py-2.5 font-semibold text-bg-primary text-sm transition-all hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-text-primary focus-visible:ring-offset-2 active:scale-[0.98]"
								onClick={action.onClick}
								type="button"
							>
								{action.label}
							</button>
						))}
					{secondaryAction &&
						(secondaryAction.href ? (
							<Link
								className="inline-flex cursor-pointer items-center gap-1 text-sm text-text-muted transition-colors hover:text-text-secondary"
								to={secondaryAction.href}
							>
								{secondaryAction.label} →
							</Link>
						) : (
							<button
								className="inline-flex cursor-pointer items-center gap-1 text-sm text-text-muted transition-colors hover:text-text-secondary"
								onClick={secondaryAction.onClick}
								type="button"
							>
								{secondaryAction.label} →
							</button>
						))}
				</div>
			)}
		</motion.div>
	)
}
